import { AlertTriangle, Camera, Check, Clock } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import type { ImageMeta } from '../../types/api';

interface ImageMetaOverlayProps {
    image: ImageMeta;
}

/**
 * ImageMetaOverlay — top-left blurry pill per UI mockup § "Top-left:
 * image meta". Shows filename + natural-image dimensions + status icon.
 *
 * Rename affordance lives in the right-rail "My Canvas" header
 * (BoxesPanel.tsx) — keeps the canvas surface focused on annotation
 * gestures and centralizes per-image metadata edits in one place.
 */
export default function ImageMetaOverlay({ image }: ImageMetaOverlayProps) {
    const { t } = useTranslation('canvas');

    const StatusIcon = image.status === 'annotated' ? Check : image.status === 'flagged' ? AlertTriangle : Clock;
    const statusColor =
        image.status === 'annotated'
            ? 'text-emerald-500'
            : image.status === 'flagged'
            ? 'text-amber-500'
            : 'text-gray-400';
    const statusLabel =
        image.status === 'annotated' ? t('status_annotated') : image.status === 'flagged' ? t('status_flagged') : t('status_pending');

    return (
        <div className="absolute top-3 left-3 z-20 flex items-center gap-2 rounded-full bg-white/90 dark:bg-gray-900/90 backdrop-blur-sm border border-gray-200 dark:border-gray-800 px-3 py-1.5 shadow-sm text-xs">
            <Camera className="h-3.5 w-3.5 text-gray-500" />
            <span className="font-medium text-gray-700 dark:text-gray-200 truncate max-w-[280px]">
                {image.filename}
            </span>
            {image.width && image.height && (
                <span className="text-gray-400">
                    · {image.width}×{image.height}
                </span>
            )}
            <span className={`flex items-center gap-1 ${statusColor}`}>
                · <StatusIcon className="h-3.5 w-3.5" /> {statusLabel}
            </span>
        </div>
    );
}
